import { HttpContextToken, HttpContext, HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { switchMap, filter, take, catchError } from 'rxjs/operators';
import { throwError, from } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { shouldExclude } from './excluded-routes.util';
import { CryptoUtil } from './crypto.util';

export const IS_RETRY = new HttpContextToken<boolean>(() => false);

/**
 * Construye los headers de autenticación firmando la petición (HMAC + RSA).
 */
function buildAuthRequest(req: import('@angular/common/http').HttpRequest<any>, authService: AuthService, isBrowser: boolean) {
  const headers: any = {};

  if (!(req.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  let bodyText = '{}';
  if (!(req.body instanceof FormData) && req.body !== null && req.body !== undefined) {
    bodyText = JSON.stringify(req.body);
  }

  const publicKey = authService.getPublicKey();

  if (publicKey) {
    const timestamp = Date.now().toString();
    const aesKey = CryptoUtil.generateRandomKey();
    
    let fullUrl = req.urlWithParams || req.url;
    let urlPath = fullUrl;
    try {
      // Extraemos solo el path (/api/...) porque Express req.originalUrl no incluye el dominio
      const origin = isBrowser ? window.location.origin : 'http://localhost';
      const u = new URL(fullUrl, origin);
      urlPath = u.pathname + u.search;
      // Preservar el '?' final si existía en la url original (URL de JS lo elimina si no hay query params)
      if (fullUrl.endsWith('?') && !urlPath.endsWith('?')) {
        urlPath += '?';
      }
    } catch (e) {}

    const method = req.method;

    const signatureHash = CryptoUtil.generateSignature(aesKey, urlPath, timestamp, method, bodyText);
    const encryptedAesKey = CryptoUtil.encryptKeyWithRSA(aesKey, publicKey);

    // Formato exacto que pediste: base64( key_encriptada + punto + firma )
    const payloadToEncode = `${encryptedAesKey}.${signatureHash}`;
    const base64Signature = btoa(payloadToEncode);

    headers['x-timestamp'] = timestamp;
    headers['x-signature'] = base64Signature;
  }

  let animalType = null;
  let animal = null;

  if (isBrowser) {
    animalType = localStorage.getItem('animalType');
    animal = localStorage.getItem('animal');
  }

  if (animal && animalType) {
    headers['Authorization'] = `${animalType} ${animal}`;
  }

  return req.clone({ setHeaders: headers, withCredentials: true });
}

export const TokenHeadersInterceptor: HttpInterceptorFn = (req, next) => {
  // Bypass de seguridad temporal (solo omite firma criptográfica, pero envía token de sesión)

  if (shouldExclude(req.url)) {
    const cleanHeaders: any = {};
    if (!(req.body instanceof FormData)) cleanHeaders['Content-Type'] = 'application/json';
    return next(req.clone({ setHeaders: cleanHeaders }));
  }

  const headers: any = {};
  if (!(req.body instanceof FormData)) headers['Content-Type'] = 'application/json';

  try {
    const animalType = localStorage.getItem('animalType');
    const animal = localStorage.getItem('animal');
    if (animal && animalType) {
      headers['Authorization'] = `${animalType} ${animal}`;
    }
  } catch (e) {
    // ignorar error en SSR
  }

  return next(req.clone({ setHeaders: headers, withCredentials: true }));
};

/* Código original deshabilitado temporalmente:
export const TokenHeadersInterceptor: HttpInterceptorFn = (req, next) => {
  if (shouldExclude(req.url)) {
    const cleanHeaders: any = {};
    if (!(req.body instanceof FormData)) cleanHeaders['Content-Type'] = 'application/json';
    return next(req.clone({ setHeaders: cleanHeaders }));
  }

  const authService = inject(AuthService);
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);

  return authService.isTokenReady.pipe(
    filter(isReady => isReady === true),
    take(1),
    switchMap(() => {
      let authReq: import('@angular/common/http').HttpRequest<any>;
      
      try {
        authReq = buildAuthRequest(req, authService, isBrowser);
      } catch (err) {
        return throwError(() => err);
      }

      return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if ((error.status === 401 || error.status === 403) && !req.context.get(IS_RETRY)) {
            return from(authService.forceRefreshSession()).pipe(
              switchMap(() => {
                const retryReq = buildAuthRequest(
                  req.clone({ context: new HttpContext().set(IS_RETRY, true) }),
                  authService,
                  isBrowser
                );
                return next(retryReq);
              })
            );
          }
          return throwError(() => error);
        })
      );
    })
  );
};
*/
